import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, LayoutGrid, FileText, Briefcase, Code, BarChart3, FileArchive, Megaphone, Palette, GraduationCap, Zap, Users, BookOpen, Globe, Lock, Wrench, FlaskConical, Microscope } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export function CategoryModal({ isOpen, onClose, currentCategory, onSave }) {
  const [selectedCategory, setSelectedCategory] = useState(currentCategory || '');
  
  // All 16 categories with icons and descriptions matching Browse page
  const categories = [
    {
      name: 'Writing & Content',
      icon: FileText,
      description: 'Create blog posts, social media content, SEO copy, and editorial content.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Business & Productivity',
      icon: Briefcase,
      description: 'Automate meeting notes, reports, emails, and project management.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Developer Tools',
      icon: Code,
      description: 'Code review, debugging, Git workflows, testing, and documentation.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Data & Analytics',
      icon: BarChart3,
      description: 'Analyze data, create visualizations, automate spreadsheets, and generate insights.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Documents & Files',
      icon: FileArchive,
      description: 'Process PDFs, Word docs, Excel files, and automate document workflows.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Marketing & Sales',
      icon: Megaphone,
      description: 'Generate ad copy, landing pages, email campaigns, and customer outreach.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Creative & Design',
      icon: Palette,
      description: 'Develop stories, characters, creative briefs, and design concepts.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Education & Learning',
      icon: GraduationCap,
      description: 'Create study guides, lesson plans, tutoring materials, and research assistance.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Communication & Support',
      icon: Users,
      description: 'Customer support, team communication, and client interactions.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Research & Knowledge',
      icon: BookOpen,
      description: 'Research synthesis, knowledge management, and information extraction.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Legal & Compliance',
      icon: Lock,
      description: 'Legal document review, compliance checking, and contract analysis.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Translation & Language',
      icon: Globe,
      description: 'Language translation, localization, and multilingual content.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Health & Wellness',
      icon: Microscope,
      description: 'Health information, wellness tracking, and medical documentation.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Finance & Accounting',
      icon: BarChart3,
      description: 'Financial analysis, accounting automation, and budget planning.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Engineering & Technical',
      icon: Wrench,
      description: 'Technical documentation, engineering workflows, and system design.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      name: 'Custom & Experimental',
      icon: FlaskConical,
      description: 'Unique workflows, experimental features, and innovative solutions.',
      color: 'bg-orange-100 dark:bg-orange-950/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    }
  ];
  
  useEffect(() => {
    setSelectedCategory(currentCategory || '');
  }, [currentCategory]);
  
  const handleSave = () => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    onSave(selectedCategory);
  };
  
  if (!isOpen) return null;
  
  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal - Expanded with scrollbar for 16 categories */}
      <div className="relative z-10 w-full max-w-5xl bg-white dark:bg-dark-bg-secondary rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-8 pb-6 border-b border-gray-200 dark:border-[#2e2e2d]">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Choose Category
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select the category that best describes your skill
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Category Grid - Scrollable */}
        <div className="overflow-y-auto px-8 py-6" style={{ maxHeight: 'calc(90vh - 220px)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => {
            const IconComponent = category.icon;
            const isSelected = selectedCategory === category.name;
            
            return (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`group relative p-6 rounded-xl text-left transition-all ${
                  isSelected
                    ? 'bg-primary text-white shadow-lg ring-2 ring-primary ring-offset-2 dark:ring-offset-dark-bg'
                    : 'bg-white dark:bg-dark-bg-tertiary hover:shadow-md border border-gray-200 dark:border-[#2e2e2d]'
                }`}
              >
                {/* Icon Circle */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  isSelected ? 'bg-white/20' : category.color
                }`}>
                  <IconComponent className={`w-6 h-6 ${
                    isSelected ? 'text-white' : category.iconColor
                  }`} />
                </div>
                
                {/* Category Name */}
                <h3 className={`text-lg font-semibold mb-2 ${
                  isSelected ? 'text-white' : 'text-gray-900 dark:text-white'
                }`}>
                  {category.name}
                </h3>
                
                {/* Description */}
                <p className={`text-sm leading-relaxed ${
                  isSelected ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {category.description}
                </p>
                
                {/* Selected Checkmark */}
                {isSelected && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
          </div>
        </div>
        
        {/* Footer - Fixed */}
        <div className="flex items-center gap-3 p-8 pt-6 border-t border-gray-200 dark:border-[#2e2e2d]">
          <Button
            onClick={handleSave}
            disabled={!selectedCategory}
            className="flex-1 bg-primary hover:bg-primary-dark text-white disabled:opacity-50"
          >
            Save Category
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
  
  // Use portal to render at root level (fixes overlay gap)
  return ReactDOM.createPortal(modalContent, document.body);
}
